// Simple auth service to replace Firebase auth
import { authAPI } from './apiService';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.listeners = [];
    this.isInitialized = false;
    this.loadUserFromStorage();
  }

  // Load user from localStorage on initialization
  loadUserFromStorage() {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        this.currentUser = JSON.parse(userData);
      } catch (error) {
        console.error('[AuthService] Error parsing user data from storage:', error);
        this.clearAuth();
      }
    }
    
    this.isInitialized = true;
  }

  // Save auth data to localStorage
  saveAuthData(token, user) {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(user));
    this.currentUser = user;
  }

  // Clear auth data
  clearAuth() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    this.currentUser = null;
  }

  // Login method
  async login(email, password) {
    try {
      const response = await authAPI.login(email, password);
      this.saveAuthData(response.token, response.user);
      this.notifyListeners(this.currentUser);
      return this.currentUser;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  // Logout method
  async logout() {
    this.clearAuth();
    this.notifyListeners(null);
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.currentUser;
  }

  // Add auth state change listener
  onAuthStateChanged(callback) {
    this.listeners.push(callback);
    
    // Only call the callback immediately if we're initialized
    if (this.isInitialized) {
      try {
        callback(this.currentUser);
      } catch (error) {
        console.error('[AuthService] Error in initial auth state callback:', error);
      }
    }
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners of auth state change
  notifyListeners(user) {
    this.listeners.forEach(callback => {
      try {
        callback(user);
      } catch (error) {
        console.error('Error in auth state listener:', error);
      }
    });
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;

// Export individual methods for compatibility
export const login = (email, password) => authService.login(email, password);
export const logout = () => authService.logout();
export const getCurrentUser = () => authService.getCurrentUser();
export const onAuthStateChanged = (callback) => authService.onAuthStateChanged(callback);
